import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Users, RefreshCw, Loader2, Plus, Trash2, AlertCircle, X, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall } from '@/lib/api';

interface TeamMember {
  user_id: string;
  email: string;
  name: string | null;
  role_id: string;
  role_name: string;
  joined_at: string;
}
interface RoleRow { id: string; name: string; description: string | null; permission_count: number }

export default function Team() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const [t, r] = await Promise.all([
        apiCall<{ items: TeamMember[] }>('/v1/team', { bearerToken: token }),
        apiCall<{ items: RoleRow[] }>('/v1/team/roles', { bearerToken: token }),
      ]);
      setItems(t.items);
      setRoles(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function changeRole(userId: string, roleId: string) {
    try {
      const token = await getToken();
      await apiCall(`/v1/team/${userId}`, { method: 'PATCH', bearerToken: token, body: { role_id: roleId } });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Role change failed'); }
  }

  async function remove(userId: string, email: string) {
    if (!confirm(`Remove ${email} from this organization?`)) return;
    try {
      const token = await getToken();
      await apiCall(`/v1/team/${userId}`, { method: 'DELETE', bearerToken: token });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Remove failed'); }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Users className="h-6 w-6" /> Team</h1>
          <p className="text-sm text-muted-foreground">
            People with dashboard access to this organization. Each role grants a different set of permissions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowInvite(true)}><Plus className="mr-1 h-4 w-4" /> Invite</Button>
        </div>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {/* Role legend */}
      <Card><CardContent className="py-3 flex items-start gap-4 flex-wrap text-xs">
        <Shield className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="space-y-1">
          {roles.map((r) => (
            <div key={r.id} className="flex items-baseline gap-2">
              <Badge variant="outline" className="capitalize">{r.name.replace('_', ' ')}</Badge>
              <span className="text-muted-foreground">{r.description}</span>
              <span className="text-muted-foreground/60 tabular-nums">({r.permission_count} perms)</span>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No team members yet. Click <strong>Invite</strong> to add one.
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0">
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-muted-foreground">
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Role</th>
              <th className="text-left px-4 py-2 font-medium">Joined</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr></thead>
            <tbody>{items.map((m) => (
              <tr key={m.user_id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-4 py-2 font-medium">{m.email}</td>
                <td className="px-4 py-2 text-muted-foreground">{m.name ?? '—'}</td>
                <td className="px-4 py-2">
                  {m.role_name === 'super_admin' ? (
                    <Badge variant="default" className="capitalize">{m.role_name.replace('_', ' ')}</Badge>
                  ) : (
                    <select
                      value={m.role_id}
                      onChange={(e) => void changeRole(m.user_id, e.target.value)}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name.replace('_', ' ')}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                  {new Date(m.joined_at.replace(' ', 'T')).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-right">
                  {m.role_name !== 'super_admin' && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => void remove(m.user_id, m.email)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>

      {showInvite && <InviteDialog roles={roles} onClose={() => setShowInvite(false)} onInvited={() => { setShowInvite(false); void load(); }} />}
    </div>
  );
}

function InviteDialog({ roles, onClose, onInvited }: { roles: RoleRow[]; onClose: () => void; onInvited: () => void }) {
  const { getToken } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!roleId) { setErr('Pick a role'); return; }
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/team', { method: 'POST', bearerToken: token, body: { email: email.trim(), name: name.trim() || null, role_id: roleId } });
      onInvited();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Invite failed'); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Invite team member</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Email *</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border rounded px-2 py-1.5 text-sm" /></label>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Name (optional)</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></label>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Role *</span>
              <select value={roleId} onChange={(e) => setRoleId(e.target.value)} required className="w-full border rounded px-2 py-1.5 text-sm">
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name.replace('_', ' ')} — {r.description}</option>)}
              </select></label>
            <p className="text-xs text-muted-foreground">
              They'll get access the first time they sign in with this email. (Email invites with magic link coming later.)
            </p>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!email.trim() || !roleId || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Invite
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
